"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Das Fehlerblatt
// ============================================================
// Was ein Bereich zeigt, wenn eine Seite beim Laden eine Ausnahme
// wirft, die niemand faengt. Bis zum 07.10.2026 gab es dafuer kein
// Blatt, und Next zeigte seine nackte Seite „Application error“ —
// englisch, ohne Rahmen, ohne Weg zurueck.
//
// EIN Baustein fuer alle Bereiche, in der Gestalt der
// `not-found.tsx` daneben: Karte, ein Satz, die Knoepfe. Jeder
// Bereich hat sein eigenes `error.tsx`, weil Anrede und Rueckweg
// sich unterscheiden (im Haus „du“ und das Dashboard, beim Kunden
// „Sie“ und sein Bereich, am geteilten Link nur ein neuer Versuch).
//
// `reset()` laesst Next das Segment noch einmal zeichnen. Der
// `digest` ist die Kennung, unter der der Server den Fehler
// protokolliert — die Meldung selbst kommt nie in den Browser,
// die Kennung aber schon, damit sich der Eintrag im Protokoll
// wiederfinden laesst.
// ============================================================

import { useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/** Was Next einem `error.tsx` reicht. */
export type FehlerblattError = Error & { digest?: string };

export function Fehlerblatt({
  error,
  reset,
  text,
  zurueck,
}: {
  error: FehlerblattError;
  reset: () => void;
  /** Der eine Satz unter dem Titel — in der Anrede des Bereichs. */
  text: string;
  /** Der Weg zurueck, wo es einen gibt. */
  zurueck?: { href: string; label: string };
}) {
  useEffect(() => {
    // Auch in der Konsole des Browsers, damit die Fehlersuche dort
    // nicht bei der Kennung aufhoert.
    console.error(error);
  }, [error]);

  return (
    <Card className="w-full max-w-md">
      <CardContent className="space-y-4 p-12 text-center">
        <p className="text-heading-sm">Da ist etwas schiefgegangen</p>
        <p className="text-body-sm text-text-secondary">{text}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button onClick={() => reset()}>Noch einmal versuchen</Button>
          {zurueck && (
            <Link href={zurueck.href}>
              <Button variant="outline">{zurueck.label}</Button>
            </Link>
          )}
        </div>
        {error.digest && (
          <p className="text-caption text-text-tertiary">
            Kennung für die Fehlersuche: {error.digest}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
